import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import HeaderBar from '../components/HeaderBar';
import CreatePatientModal from '../components/CreatePatientModal';
import { patientsService, Patient, CreatePatientData } from '../services/patientsService';
import { useAuth } from '../hooks/useAuth';

const PatientsList: React.FC = () => {
  const [search, setSearch] = useState('');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const patientsData = await patientsService.getPatients();
      setPatients(patientsData);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePatient = async (data: CreatePatientData) => {
    try {
      await patientsService.createPatient(data);
      // Close the modal
      setShowCreateModal(false);
      // Refresh the patient list from the API
      await fetchPatients();
    } catch (error) {
      console.error('Failed to create patient:', error);
      throw error; // Re-throw to let the modal handle the error display
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  // Helper function to get full name
  const getFullName = (patient: Patient) => {
    return `${patient.firstName} ${patient.lastName}`.trim() || patient.username;
  };

  const filtered = patients
    .filter((patient: Patient) => {
      const fullName = getFullName(patient);
      return fullName.toLowerCase().includes(search.toLowerCase()) ||
             patient.email.toLowerCase().includes(search.toLowerCase()) ||
             patient.username.toLowerCase().includes(search.toLowerCase());
    })
    .sort((a: Patient, b: Patient) => {
      return getFullName(a).localeCompare(getFullName(b));
    });

  return (
    <div style={{ background: '#f9fafb', minHeight: '100vh', padding: '0' }}>
      <HeaderBar title="Mis pacientes" rightContent={<button onClick={handleLogout} style={{background:'none',border:'none',color:'#38b2ac',fontWeight:600,fontSize:16,cursor:'pointer'}}>Logout</button>} />
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 40 }}>
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Buscar por nombre, email o usuario"
          />
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              background: '#38b2ac',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 20px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 2px 4px rgba(56, 178, 172, 0.2)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#319795';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(56, 178, 172, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#38b2ac';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(56, 178, 172, 0.2)';
            }}
          >
            <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>
            <span>Nuevo Paciente</span>
          </button>
        </div>
        {loading ? (
          <div style={{ padding: 40, fontSize: 18, color: '#666', textAlign: 'center' }}>
            Loading patients...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, fontSize: 18, color: '#666', textAlign: 'center' }}>
            No patients found
          </div>
        ) : (
          <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    PACIENTE
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    EMAIL
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    TELÉFONO
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    ACCIONES
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((patient: Patient, index: number) => (
                  <tr 
                    key={patient.id}
                    style={{ 
                      borderBottom: index < filtered.length - 1 ? '1px solid #f1f5f9' : 'none',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                    onClick={() => navigate(`/app/patient/${patient.id}`)}
                  >
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%', 
                          background: patient.avatarUrl ? `url(${patient.avatarUrl})` : '#38b2ac',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          fontSize: 16, 
                          fontWeight: 600, 
                          color: '#fff',
                          flexShrink: 0
                        }}>
                          {!patient.avatarUrl && getFullName(patient).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 16 }}>
                            {getFullName(patient)}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: 14 }}>
                            @{patient.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: 14 }}>
                      {patient.email}
                    </td>
                    <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: 14 }}>
                      {patient.phone_number || '-'}
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/app/patient/${patient.id}`);
                        }}
                        style={{
                          background: '#38b2ac',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          padding: '6px 16px',
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#319795';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#38b2ac';
                        }}
                      >
                        Ver perfil
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Patient Modal */}
      <CreatePatientModal
        open={showCreateModal}
        onSave={handleCreatePatient}
        onCancel={() => setShowCreateModal(false)}
      />
    </div>
  );
};

export default PatientsList; 